import { atom } from 'nanostores'

const STORAGE_KEY = 'birthday_boosting_banner_dismissed'

/** Reads the persisted one-time dismissal flag, tolerating storage failures. */
export function readBannerDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/** Reactive mirror of the persisted flag, seeded from localStorage on load. */
export const $isBoostingBannerDismissed = atom<boolean>(readBannerDismissed())

/** Transient flag — true while the banner is playing its fly-to-chip exit
 *  animation. The booster chip in the top nav mounts on this OR on the
 *  persisted dismissal so during the hand-off both elements are on screen
 *  long enough for the chip's pop-in to overlap the banner's flight. */
export const $isBoostingBannerExiting = atom<boolean>(false)

/** Banner flight duration in ms. Exported so the banner component can match
 *  its CSS transition to the store's dismiss timeout. */
export const BOOSTING_BANNER_EXIT_FLIGHT_MS = 620

/** Dismisses the banner permanently (persisted) and updates the store. */
export function dismissBoostingBanner(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1')
  } catch {
    // Ignore storage failures (private mode / quota); the banner just reappears.
  }
  $isBoostingBannerDismissed.set(true)
}

/** Kicks off the fly-to-chip exit animation. Persisted dismissal fires once
 *  the banner has reached the chip's position; the exiting flag lingers a
 *  moment longer so the chip's pop-in animation can run to completion
 *  without its class being yanked off mid-flight. No-op if already dismissed
 *  or already animating. */
export function startBoostingBannerExit(): void {
  if ($isBoostingBannerDismissed.get() || $isBoostingBannerExiting.get()) {
    return
  }
  $isBoostingBannerExiting.set(true)
  setTimeout(() => dismissBoostingBanner(), BOOSTING_BANNER_EXIT_FLIGHT_MS)
  setTimeout(
    () => $isBoostingBannerExiting.set(false),
    BOOSTING_BANNER_EXIT_FLIGHT_MS + 380,
  )
}
